import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { StudentQuestion, StudentTask } from '@/api/ieltsMock';
import { questionNumber } from '@/lib/mockReview';
import { cn } from '@/lib/utils';

/**
 * Сетка Matching information (`lib/matchGrid.ts`). На компьютере — таблица «утверждение × буква» с отметкой
 * в клетке, как на бланке. На узком экране таблица с 5–7 столбцами сжимала бы утверждения до слова в строке —
 * поэтому утверждение целиком, под ним ряд букв-кружков под палец (h-10). Значение — буква варианта.
 */
export function MatchGrid({
  task,
  letters,
  answers,
  onAnswer,
  narrow,
  num,
}: {
  task: StudentTask;
  letters: string[];
  answers: Record<string, string>;
  onAnswer: (qid: string, val: string) => void;
  narrow: boolean;
  num: (q: StudentQuestion) => ReactNode;
}) {
  const { t } = useTranslation();
  const n = (q: StudentQuestion) => questionNumber(q.ord, q.ord_to);

  if (narrow) {
    return (
      <ol className="space-y-4">
        {task.questions.map((q) => {
          const val = answers[q.id] ?? '';
          return (
            <li key={q.id} className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-on-surface">
                {num(q)}
                <span className="pt-0.5">{q.prompt}</span>
              </div>
              <div role="radiogroup" aria-label={t('ieltsMock.gridLetters', { n: n(q) })} className="flex flex-wrap gap-2 pl-4">
                {letters.map((l) => (
                  <label
                    key={l}
                    className={cn(
                      'inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border text-sm font-semibold focus-within:ring-2 focus-within:ring-primary',
                      val === l ? 'border-primary bg-primary text-on-primary' : 'border-outline bg-surface text-on-surface',
                    )}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      checked={val === l}
                      onChange={() => onAnswer(q.id, l)}
                      aria-label={t('ieltsMock.gridCell', { n: n(q), l })}
                      className="sr-only"
                    />
                    {l}
                  </label>
                ))}
              </div>
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm text-on-surface">
        <thead>
          <tr>
            <th scope="col" className="border border-outline px-3 py-2 text-left">
              <span className="sr-only">{t('ieltsMock.gridStatement')}</span>
            </th>
            {letters.map((l) => (
              <th key={l} scope="col" className="w-10 border border-outline px-1 py-2 text-center font-semibold">
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {task.questions.map((q) => {
            const val = answers[q.id] ?? '';
            return (
              <tr key={q.id}>
                <th scope="row" className="border border-outline px-3 py-2 text-left font-normal">
                  <div className="flex items-start gap-2">
                    {num(q)}
                    <span className="pt-0.5">{q.prompt}</span>
                  </div>
                </th>
                {letters.map((l) => (
                  <td key={l} className={cn('border border-outline p-0 text-center', val === l && 'bg-primary/10')}>
                    <label className="flex h-10 cursor-pointer items-center justify-center">
                      <input
                        type="radio"
                        name={q.id}
                        checked={val === l}
                        onChange={() => onAnswer(q.id, l)}
                        aria-label={t('ieltsMock.gridCell', { n: n(q), l })}
                        className="h-4 w-4 accent-primary"
                      />
                    </label>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
